import { useEffect, useRef, useState, type ReactNode } from "react";
import type { CaseDetail, Company } from "@rma-orbit/shared";
import { todayKst } from "@rma-orbit/shared";

import { ApiError, api } from "../api/client.js";
import { navigate } from "../App.js";
import { StatusBadge } from "../lib/ui.js";

/**
 * 에이전트에게 묻기 (2026-09-09 대표 지시).
 *
 * 검색란을 직접 뒤지지 않고 채팅으로 묻는다. 지금 답하는 것은 **이 사이트가 이미 아는 것**이다 —
 * 케이스·장비·교체·출고·달력·협력사(서버 API), 콘솔이 학습한 장비 명령(브라우저). 질문을 규칙으로
 * 읽어 해당 API 를 부르고, 답마다 **어디서 왔는지**(출처 링크)를 붙인다.
 *
 * ★ 언어 모델은 붙어 있지 않다(개발자용 사실 — ROADMAP C80·C83). 답은 자료를 그대로 보여 주는 것이지
 *   추론이 아니다. 화면은 이 사실을 말하지 않는다(대표 결정 C83) — 답과 출처만 보여 준다.
 */

interface Msg {
  role: "me" | "agent";
  text: string;
  body?: ReactNode;
  sources?: Array<{ label: string; hash: string }>;
  /** 모델 답 — 본문(body)이 요약·자료를 다 그리므로 text 를 따로 찍지 않는다 */
  model?: boolean;
  at: string;
}

const SUGGEST = [
  "오늘 할 일 뭐야?",
  "내일 할 일 알려줘",
  "JTM2UB0130583 어디 있어?",
  "이번 주 입고 몇 건이야?",
  "SECUI 체류 중인 건 알려줘",
  "승인 대기 몇 건?",
  "나래넷츠 케이스 보여줘",
  "이 장비 show 명령 뭐 있어?",
];

const SERIAL_RE = /\b(JT[A-Z0-9]{9,14})\b/i;
const CASE_RE = /\b(RMA-\d{4}-\d{4})\b/i;

const STATUS_WORDS: Array<[RegExp, string, string]> = [
  [/(secui|시큐아이).{0,6}(체류|가 있|이관)/i, "AT_VENDOR", "SECUI 체류"],
  [/회수/, "VENDOR_BACK", "SECUI 회수"],
  [/반송 ?준비/, "READY_TO_RETURN", "반송 준비"],
  [/반송 ?완료|돌려보낸|끝난/, "RETURNED", "반송 완료"],
  [/검수 ?중/, "INSPECTING", "검수 중"],
  [/입고 ?접수|접수된/, "RECEIVED", "입고 접수"],
];

function pad(n: number) {
  return String(n).padStart(2, "0");
}
function dayOf(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function rangeOf(text: string): { from: string; to: string; label: string } | null {
  const today = todayKst();
  const t = new Date(`${today}T00:00:00`);
  if (/오늘/.test(text)) return { from: today, to: today, label: "오늘" };
  if (/모레/.test(text)) { const d = new Date(t); d.setDate(d.getDate() + 2); return { from: dayOf(d), to: dayOf(d), label: "모레" }; }
  if (/내일/.test(text)) { const d = new Date(t); d.setDate(d.getDate() + 1); return { from: dayOf(d), to: dayOf(d), label: "내일" }; }
  if (/어제/.test(text)) { const d = new Date(t); d.setDate(d.getDate() - 1); return { from: dayOf(d), to: dayOf(d), label: "어제" }; }
  if (/다음 ?주/.test(text)) { const s0 = new Date(t); s0.setDate(s0.getDate() - s0.getDay() + 7); const e = new Date(s0); e.setDate(s0.getDate() + 6); return { from: dayOf(s0), to: dayOf(e), label: "다음 주" }; }
  if (/이번 ?주|금주/.test(text)) { const d = new Date(t); d.setDate(d.getDate() - d.getDay()); const e = new Date(d); e.setDate(d.getDate() + 6); return { from: dayOf(d), to: dayOf(e), label: "이번 주" }; }
  if (/지난 ?주/.test(text)) { const s = new Date(t); s.setDate(s.getDate() - s.getDay() - 7); const e = new Date(s); e.setDate(s.getDate() + 6); return { from: dayOf(s), to: dayOf(e), label: "지난 주" }; }
  if (/이번 ?달|이달|금월/.test(text)) return { from: `${today.slice(0, 7)}-01`, to: today, label: "이번 달" };
  if (/지난 ?달|전월/.test(text)) { const [y, m] = today.split("-").map(Number); return { from: dayOf(new Date(y!, m! - 2, 1)), to: dayOf(new Date(y!, m! - 1, 0)), label: "지난 달" }; }
  const m = /(\d{1,2})월 ?(\d{1,2})일/.exec(text);
  if (m) { const y = today.slice(0, 4); const d = `${y}-${pad(Number(m[1]))}-${pad(Number(m[2]))}`; return { from: d, to: d, label: `${m[1]}월 ${m[2]}일` }; }
  return null;
}

function caseLink(c: { id: number; case_no: string }) {
  return (
    <a href={`#/cases/${c.id}`} onClick={(e) => { e.preventDefault(); navigate(`#/cases/${c.id}`); }}>
      {c.case_no}
    </a>
  );
}

function CaseRows({ items }: { items: CaseDetail[] }) {
  return (
    <div className="tablewrap" style={{ marginTop: 6 }}>
      <table>
        <thead><tr><th>번호</th><th>입고</th><th>출고</th><th>시리얼</th><th>상태</th><th>교체</th></tr></thead>
        <tbody>
          {items.slice(0, 12).map((c) => (
            <tr key={c.id}>
              <td>{caseLink(c)}</td>
              <td className="num">{c.received_at}</td>
              <td className="num">{c.shipped_at ? `${c.shipped_at}${c.shipped_to ? ` → ${c.shipped_to}` : ""}` : "—"}</td>
              <td className="mono">{c.serial_norm}</td>
              <td><StatusBadge status={c.status} /></td>
              <td className="mono">{c.replacement_serial ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {items.length > 12 && <div className="muted" style={{ padding: "6px 10px", fontSize: 12 }}>… 외 {items.length - 12}건</div>}
    </div>
  );
}

async function answer(text: string, companies: Company[]): Promise<Omit<Msg, "role" | "at">> {
  const q = text.trim();

  // 1) 시리얼
  const sm = SERIAL_RE.exec(q);
  if (sm) {
    const serial = sm[1]!.toUpperCase();
    const res = await api.cases({ serial, limit: 50 });
    if (res.total === 0) {
      return { text: `${serial} 이 들어간 시리얼은 케이스에 없습니다. 원 시리얼·교체 시리얼 양쪽을 봤습니다. 0건은 "없다" 가 아니라 "못 찾았다" 일 수 있어요 — 다른 글자로 다시 물어봐 주세요.`, sources: [{ label: "케이스 검색", hash: `#/cases` }] };
    }
    const latest = res.items[0]!;
    const where =
      latest.status === "AT_VENDOR" ? `SECUI 에 가 있습니다${latest.shipped_at ? ` (${latest.shipped_at} 출고)` : ""}` :
      latest.status === "RETURNED" ? `협력사로 돌아갔습니다${latest.shipped_at ? ` (${latest.shipped_at} → ${latest.shipped_to ?? "?"})` : ""}` :
      latest.status === "READY_TO_RETURN" ? "반송 준비가 끝나 사무실에 있습니다" :
      latest.status === "VENDOR_BACK" ? "SECUI 에서 회수돼 사무실에 있습니다" :
      "사무실에 있습니다 (검수 전/중)";
    return {
      text: `${serial} — 케이스 ${res.total}건. 가장 최근 ${latest.case_no} 은 ${where}.${latest.replacement_serial ? ` 교체 시리얼은 ${latest.replacement_serial} 입니다.` : ""}`,
      body: <CaseRows items={res.items} />,
      sources: [{ label: `장비 ${serial} 이력`, hash: `#/devices/${encodeURIComponent(serial)}` }, { label: "케이스 목록", hash: "#/cases" }],
    };
  }

  // 2) 케이스 번호
  const cm = CASE_RE.exec(q);
  if (cm) {
    const no = cm[1]!.toUpperCase();
    const res = await api.cases({ limit: 200 });
    const hit = res.items.find((c) => c.case_no === no);
    if (!hit) return { text: `${no} 케이스를 못 찾았습니다.`, sources: [{ label: "케이스 목록", hash: "#/cases" }] };
    return {
      text: `${no}: ${hit.serial_norm}, ${hit.received_at} 입고, 상태 ${hit.status}${hit.shipped_at ? `, ${hit.shipped_at} 출고 → ${hit.shipped_to ?? "?"}` : ""}${hit.replacement_serial ? `, 교체 ${hit.replacement_serial}` : ""}. 증상: ${hit.reported_symptom ?? "—"}`,
      body: <CaseRows items={[hit]} />,
      sources: [{ label: `${no} 상세`, hash: `#/cases/${hit.id}` }],
    };
  }

  // 3) 명령체계 (콘솔이 학습한 것)
  if (/명령|command|show|help|cli/i.test(q)) {
    let learned: Array<{ vendor: string; model: string; commands: Array<{ name: string; help: string; seen: number }>; simulated: boolean }> = [];
    try { learned = JSON.parse(localStorage.getItem("orbit.cli.learned") ?? "[]"); } catch { /* 없음 */ }
    if (learned.length === 0) {
      return { text: "아직 학습된 장비 명령이 없습니다. [콘솔·증적] 에서 장비에 붙어 명령체계 학습을 한 번 돌려 주세요.", sources: [{ label: "콘솔·증적", hash: "#/console" }] };
    }
    const word = /\b([a-z][a-z-]{1,20})\b/i.exec(q.replace(/명령|command|help|cli|뭐|있어|알려|줘/gi, ""))?.[1]?.toLowerCase();
    const rows = learned.flatMap((d) => d.commands.filter((c) => !word || c.name.includes(word) || c.help.toLowerCase().includes(word)).map((c) => ({ ...c, dev: `${d.vendor} ${d.model}${d.simulated ? " (견본)" : ""}` })));
    if (rows.length === 0) return { text: `학습된 ${learned.length}개 장비에서 "${word}" 에 맞는 명령을 못 찾았습니다.`, sources: [{ label: "콘솔·증적", hash: "#/console" }] };
    return {
      text: `${word ? `"${word}" 관련 ` : ""}명령 ${rows.length}개를 찾았습니다 (학습된 장비 ${learned.length}).`,
      body: (
        <div className="learn-grid" style={{ marginTop: 6 }}>
          {rows.slice(0, 12).map((r, i) => (
            <div key={i} className="learn-cmd"><span className="mono">{r.name}</span><span className="muted">{r.help} · {r.dev}</span><span className="badge">{r.seen}회</span></div>
          ))}
        </div>
      ),
      sources: [{ label: "콘솔·증적 (학습)", hash: "#/console" }],
    };
  }

  // 4) 할 일 — 그날 일정 + 지금 줄 서 있는 일
  if (/할 ?일|해야|뭐 ?해|할것|todo|일정 ?있/i.test(q)) {
    const range = rangeOf(q) ?? { from: todayKst(), to: todayKst(), label: "오늘" };
    const [cal, pending, awaiting, ready, received, inspecting] = await Promise.all([
      api.calendar(range.from, range.to),
      api.cases({ approval_pending: 1, limit: 30 }),
      api.cases({ status: "AT_VENDOR", swap: "none", limit: 30 }),
      api.cases({ status: "READY_TO_RETURN", limit: 30 }),
      api.cases({ status: "RECEIVED", limit: 30 }),
      api.cases({ status: "INSPECTING", limit: 30 }),
    ]);
    const shipsBack = cal.events.filter((e) => e.kind === "shipped_owner" || e.kind === "shipped_vendor");
    const arrivals = cal.events.filter((e) => e.kind === "received");
    const isFuture = range.from > todayKst();
    const queues: Array<{ label: string; total: number; hash: string; hint: string; items: CaseDetail[] }> = [
      { label: "승인 받을 것", total: pending.total, hash: "#/cases", hint: "증상 보고 메일 → 부장님 확인 → 일괄 승인", items: pending.items },
      { label: "송장 붙일 것", total: ready.total, hash: "#/shipping", hint: "반송 준비가 끝난 건 — 로젠 송장", items: ready.items },
      { label: "교체 시리얼 확인할 것", total: awaiting.total, hash: "#/cases?swap=none", hint: "SECUI 에 가 있는데 교체품이 아직", items: awaiting.items },
      { label: "검수 시작할 것", total: received.total, hash: "#/cases", hint: "입고만 되고 검수 전", items: received.items },
      { label: "검수 중", total: inspecting.total, hash: "#/cases", hint: "콘솔·증적에서 로그를 남기고 결과를 누른다", items: inspecting.items },
    ].filter((x) => x.total > 0);
    const totalTodo = cal.items.length + queues.reduce((n, x) => n + x.total, 0);
    return {
      text: `${range.label}${range.from !== range.to ? `(${range.from} ~ ${range.to})` : `(${range.from})`} 할 일 — 적어 둔 일정 ${cal.items.length}건${isFuture ? "" : `, 지금 줄 서 있는 일 ${queues.reduce((n, x) => n + x.total, 0)}건`}${totalTodo === 0 ? ". 비어 있습니다." : "."}`,
      body: (
        <div className="todo">
          {cal.items.length > 0 && (
            <div className="todo-sec">
              <div className="todo-h">📌 적어 둔 일정</div>
              <ul>{cal.items.map((it) => <li key={it.id}>{range.from !== range.to ? `${it.on_date} ` : ""}<strong>{it.title}</strong>{it.case_no ? <> · {caseLink({ id: it.case_id!, case_no: it.case_no })}</> : null}{it.body ? <span className="muted"> — {it.body}</span> : null}</li>)}</ul>
            </div>
          )}
          {(arrivals.length > 0 || shipsBack.length > 0) && (
            <div className="todo-sec">
              <div className="todo-h">📦 {range.label} 기록된 입고·출고</div>
              <ul>
                {arrivals.slice(0, 8).map((e, i) => <li key={`a${i}`}>입고 {caseLink({ id: e.case_id, case_no: e.case_no })} <span className="mono">{e.serial_norm}</span>{e.company ? ` · ${e.company}` : ""}</li>)}
                {shipsBack.slice(0, 8).map((e, i) => <li key={`s${i}`}>{e.kind === "shipped_vendor" ? "SECUI행" : "협력사행"} {caseLink({ id: e.case_id, case_no: e.case_no })} <span className="mono">{e.serial_norm}</span>{e.company ? ` → ${e.company}` : ""}</li>)}
              </ul>
            </div>
          )}
          {!isFuture && queues.map((qx) => (
            <div className="todo-sec" key={qx.label}>
              <div className="todo-h">
                ✅ {qx.label} <span className="badge">{qx.total}</span> <span className="muted" style={{ fontWeight: 400 }}>— {qx.hint}</span>
              </div>
              <ul>{qx.items.slice(0, 5).map((c) => <li key={c.id}>{caseLink(c)} <span className="mono">{c.serial_norm}</span>{c.reported_symptom ? <span className="muted"> · {c.reported_symptom.slice(0, 40)}</span> : null}</li>)}{qx.total > 5 && <li className="muted">… 외 {qx.total - 5}건</li>}</ul>
            </div>
          ))}
          {isFuture && queues.length > 0 && (
            <div className="muted" style={{ fontSize: 12 }}>지금 줄 서 있는 일(승인 {pending.total} · 송장 {ready.total} · 교체 확인 {awaiting.total})은 "오늘 할 일" 로 물으면 보여 줍니다.</div>
          )}
        </div>
      ),
      sources: [{ label: "달력", hash: "#/calendar" }, { label: "대시보드", hash: "#/" }, { label: "택배 송장", hash: "#/shipping" }],
    };
  }

  // 5) 기간 + 사건
  const range = rangeOf(q);
  if (range && /입고|출고|교체|일정|나간|들어온|받은|보낸/.test(q)) {
    const cal = await api.calendar(range.from, range.to);
    const kind = /출고|나간|보낸/.test(q) ? ["shipped_vendor", "shipped_owner"] : /교체/.test(q) ? ["swapped"] : /일정/.test(q) ? [] : ["received"];
    const ev = cal.events.filter((e) => kind.includes(e.kind));
    const label = kind.length === 0 ? "일정" : kind[0] === "received" ? "입고" : kind[0] === "swapped" ? "교체" : "출고";
    const n = kind.length === 0 ? cal.items.length : ev.length;
    return {
      text: `${range.label}(${range.from}${range.from !== range.to ? ` ~ ${range.to}` : ""}) ${label} ${n}건입니다.`,
      body: kind.length === 0 ? (
        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{cal.items.map((s) => <li key={s.id}>{s.on_date} {s.title}{s.case_no ? ` · ${s.case_no}` : ""}</li>)}</ul>
      ) : (
        <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
          {ev.slice(0, 15).map((e, i) => <li key={i}>{e.on_date} {caseLink({ id: e.case_id, case_no: e.case_no })} <span className="mono">{e.serial_norm}</span>{e.company ? ` · ${e.company}` : ""}</li>)}
          {ev.length > 15 && <li className="muted">… 외 {ev.length - 15}건</li>}
        </ul>
      ),
      sources: [{ label: "달력", hash: "#/calendar" }],
    };
  }

  // 5) 승인 대기
  if (/승인 ?대기|승인 ?안 ?된|결재/.test(q)) {
    const res = await api.cases({ approval_pending: 1, limit: 50 });
    return { text: `승인을 아직 못 받은 건은 ${res.total}건입니다.`, body: <CaseRows items={res.items} />, sources: [{ label: "케이스 목록", hash: "#/cases" }] };
  }

  // 6) 교체 대기
  if (/교체 ?(대기|안 ?된|미확인)/.test(q)) {
    const res = await api.cases({ status: "AT_VENDOR", swap: "none", limit: 50 });
    return { text: `SECUI 에 가 있는데 아직 교체 시리얼이 안 붙은 건은 ${res.total}건입니다.`, body: <CaseRows items={res.items} />, sources: [{ label: "★ 교체 대기", hash: "#/cases?swap=none" }] };
  }

  // 7) 상태
  for (const [re, status, label] of STATUS_WORDS) {
    if (re.test(q)) {
      const res = await api.cases({ status, limit: 50 });
      return { text: `${label} 상태는 ${res.total}건입니다.`, body: <CaseRows items={res.items} />, sources: [{ label: "케이스 목록", hash: "#/cases" }] };
    }
  }

  // 8) 협력사
  const co = companies.find((c) => c.name.length >= 2 && q.includes(c.name)) ?? companies.find((c) => c.aliases.some((a) => a.length >= 2 && q.includes(a)));
  if (co) {
    const res = await api.cases({ company: String(co.id), limit: 50 });
    const open = res.items.filter((c) => c.status !== "RETURNED").length;
    return { text: `${co.name}: 케이스 ${res.total}건, 아직 안 끝난 건 ${open}건입니다.${co.contact ? ` 연락처/주소: ${co.contact}` : ""}`, body: <CaseRows items={res.items} />, sources: [{ label: "협력사", hash: "#/companies" }, { label: "케이스 목록", hash: "#/cases" }] };
  }

  // 9) 전체 요약
  if (/전체|요약|현황|지금 어디|대시보드/.test(q)) {
    const [a, b, c, d] = await Promise.all([
      api.cases({ status: "RECEIVED", limit: 1 }), api.cases({ status: "INSPECTING", limit: 1 }),
      api.cases({ status: "AT_VENDOR", limit: 1 }), api.cases({ status: "READY_TO_RETURN", limit: 1 }),
    ]);
    return { text: `입고 접수 ${a.total} · 검수 중 ${b.total} · SECUI 체류 ${c.total} · 반송 준비 ${d.total}.`, sources: [{ label: "대시보드", hash: "#/" }] };
  }

  return {
    text: "그 질문은 이렇게 물어 주세요 — 시리얼(JTM…), 케이스 번호(RMA-2026-0001), 협력사 이름, 기간+입고/출고(이번 주 입고), 상태(SECUI 체류), 승인 대기, 교체 대기, 장비 명령(show) 으로 물어봐 주세요. 언어 모델은 아직 붙어 있지 않아 자료를 찾아 보여 주는 것까지만 합니다.",
  };
}

type AgentRow = { id: number; case_no: string; serial_norm: string; replacement_serial: string | null; status: string; received_at: string; shipped_at: string | null; shipped_to: string | null };

const QUEUE_LABELS: Array<[string, string, string]> = [
  ["approval_pending", "승인 받을 것", "#/cases"],
  ["ready_to_ship", "송장 붙일 것", "#/shipping"],
  ["awaiting_replacement_serial", "교체 시리얼 확인할 것", "#/cases?swap=none"],
  ["received_not_inspected", "검수 시작할 것", "#/cases"],
  ["inspecting", "검수 중", "#/cases"],
];

/** 도구가 돌려준 자료를 어제의 규칙 답과 같은 모양으로 그린다 — 표·일정·큐. 모델은 요약만 했다 */
function ToolBlocks({ results }: { results: Array<{ name: string; args: Record<string, unknown>; result?: unknown }> }) {
  const blocks: ReactNode[] = [];
  results.forEach((r, i) => {
    const res = (r.result ?? {}) as Record<string, unknown>;
    if (r.name === "search_cases" && Array.isArray(res.items)) {
      const items = res.items as AgentRow[];
      blocks.push(
        <div key={i} className="todo-sec">
          <div className="todo-h">🗂 케이스 {typeof res.total === "number" ? `${res.total}건` : ""}{typeof res.note === "string" ? <span className="muted" style={{ fontWeight: 400 }}> — {res.note}</span> : null}</div>
          {items.length > 0 ? <CaseRows items={items as unknown as CaseDetail[]} /> : <div className="muted">조건에 맞는 케이스가 없습니다 — 0건은 "없다" 가 아니라 "못 찾았다" 일 수 있습니다.</div>}
        </div>,
      );
    } else if (r.name === "get_case" && typeof res.case_no === "string") {
      blocks.push(<div key={i} className="todo-sec"><div className="todo-h">🗂 케이스</div><CaseRows items={[res as unknown as CaseDetail]} /></div>);
    } else if (r.name === "calendar") {
      const schedules = (res.schedules ?? []) as Array<{ on_date: string; title: string; body: string | null; case_no: string | null }>;
      const events = (res.events ?? []) as Array<{ on_date: string; kind: string; case_no: string; serial: string; company: string | null }>;
      const kindLabel: Record<string, string> = { received: "입고", shipped_vendor: "SECUI행", shipped_owner: "협력사행", swapped: "교체" };
      blocks.push(
        <div key={i} className="todo-sec">
          <div className="todo-h">📅 {String(r.args.from ?? "")} ~ {String(r.args.to ?? "")}</div>
          {schedules.length > 0 && <ul>{schedules.map((s, j) => <li key={`s${j}`}>{s.on_date} <strong>{s.title}</strong>{s.case_no ? ` · ${s.case_no}` : ""}{s.body ? <span className="muted"> — {s.body}</span> : null}</li>)}</ul>}
          {events.length > 0 && <ul>{events.slice(0, 20).map((e, j) => <li key={`e${j}`}>{e.on_date} {kindLabel[e.kind] ?? e.kind} <a href="#/cases" onClick={(ev) => { ev.preventDefault(); navigate("#/cases"); }}>{e.case_no}</a> <span className="mono">{e.serial}</span>{e.company ? ` · ${e.company}` : ""}</li>)}{events.length > 20 && <li className="muted">… 외 {events.length - 20}건</li>}</ul>}
          {schedules.length === 0 && events.length === 0 && <div className="muted">이 기간에는 기록이 없습니다.</div>}
        </div>,
      );
    } else if (r.name === "today_queues") {
      blocks.push(
        <div key={i} className="todo-sec">
          <div className="todo-h">✅ 지금 줄 서 있는 일</div>
          <div className="chips">
            {QUEUE_LABELS.map(([k, label, hash]) => (
              <button key={k} className="chip" onClick={() => navigate(hash)}>{label} <span className="badge">{String(res[k] ?? 0)}</span></button>
            ))}
          </div>
        </div>,
      );
    } else if (r.name === "device_history" && res.device) {
      const events = (res.events ?? []) as Array<Record<string, unknown>>;
      const serial = String((res.device as { serial_norm?: string }).serial_norm ?? r.args.serial ?? "");
      blocks.push(
        <div key={i} className="todo-sec">
          <div className="todo-h">🔧 장비 <span className="mono">{serial}</span> 이력 {events.length}건</div>
          <ul>{events.slice(0, 12).map((e, j) => <li key={j}><span className="num">{String(e.at ?? "—").slice(0, 10)}</span> {String(e.kind)}{e.case_no ? ` · ${String(e.case_no)}` : ""}{e.detail ? <span className="muted"> — {String(e.detail).slice(0, 60)}</span> : null}</li>)}</ul>
        </div>,
      );
    } else if (r.name === "companies" && Array.isArray(r.result)) {
      const list = r.result as Array<{ id: number; name: string; kind: string | null }>;
      blocks.push(<div key={i} className="todo-sec"><div className="todo-h">◎ 협력사 {list.length}곳</div><div className="chips">{list.slice(0, 30).map((c) => <span key={c.id} className="chip">{c.name}{c.kind === "VENDOR" ? " (벤더)" : ""}</span>)}</div></div>);
    }
  });
  if (blocks.length === 0) return null;
  return <div className="todo">{blocks}</div>;
}

/** 모델 답 — 줄 단위로 그린다. '- ' 줄은 목록, 나머지는 문단. 케이스 번호는 링크 */
function ModelAnswer({ text }: { text: string }) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
  const out: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => {
    if (list.length) {
      out.push(<ul key={`ul${out.length}`} style={{ margin: "4px 0", paddingLeft: 18 }}>{list.map((l, i) => <li key={i}>{linkify(l)}</li>)}</ul>);
      list = [];
    }
  };
  for (const l of lines) {
    if (/^\s*[-•*]\s+/.test(l)) list.push(l.replace(/^\s*[-•*]\s+/, ""));
    else { flush(); out.push(<div key={`p${out.length}`} style={{ margin: "2px 0" }}>{linkify(l)}</div>); }
  }
  flush();
  return <div className="model-answer">{out}</div>;
}

function linkify(line: string): ReactNode {
  const parts = line.split(/(RMA-\d{4}-\d{4})/g);
  if (parts.length === 1) return line.replace(/\*\*/g, "");
  return parts.map((p, i) =>
    /^RMA-\d{4}-\d{4}$/.test(p)
      ? <a key={i} href="#/cases" onClick={(e) => { e.preventDefault(); navigate("#/cases"); }}>{p}</a>
      : p.replace(/\*\*/g, ""),
  );
}

export function Agent() {
  const [msgs, setMsgs] = useState<Msg[]>([
    { role: "agent", at: new Date().toISOString(), text: "무엇을 찾을까요? 이 사이트가 아는 것(케이스·장비·교체·출고·달력·협력사·학습된 장비 명령)을 찾아서 출처와 함께 답합니다." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    api.companies().then(setCompanies).catch(() => setCompanies([]));
  }, []);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs.length, busy]);

  async function ask(text: string) {
    const q = text.trim();
    if (!q || busy) return;
    setInput("");
    setMsgs((m) => [...m, { role: "me", text: q, at: new Date().toISOString() }]);
    setBusy(true);
    try {
      // 1) 사이트 안의 모델(도구로 자료를 읽는다). 2) 모델이 없거나(503) 막히면 규칙 답으로.
      const history = msgs
        .filter((m) => !m.body)
        .slice(-8)
        .map((m) => ({ role: m.role === "me" ? ("user" as const) : ("model" as const), text: m.text }));
      let a: Omit<Msg, "role" | "at"> | null = null;
      try {
        const r = await api.agentAsk(q, history);
        // 모델은 요약만, 자료는 도구 결과로 사이트가 그린다 (C86). text 는 다음 질문의 맥락
        a = {
          text: r.answer,
          body: (
            <>
              <ModelAnswer text={r.answer} />
              <ToolBlocks results={r.tool_results} />
              {r.unverified.length > 0 && (
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  ⚠ 자료에 없는 번호를 지웠습니다: {r.unverified.join(", ")}
                </div>
              )}
            </>
          ),
          sources: r.sources,
        };
      } catch (err) {
        if (!(err instanceof ApiError && err.status === 503)) throw err;
      }
      if (a === null) a = await answer(q, companies);
      else a = { ...a, model: true };
      setMsgs((m) => [...m, { role: "agent", at: new Date().toISOString(), ...a! }]);
    } catch (err) {
      setMsgs((m) => [...m, { role: "agent", at: new Date().toISOString(), text: `찾다가 실패했습니다: ${err instanceof Error ? err.message : String(err)}` }]);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="admin-head">
        <span className="navicon" aria-hidden="true">✦</span>
        <div>
          <h1>에이전트에게 묻기</h1>
          <p className="page-desc" style={{ margin: 0 }}>
            검색란을 뒤지지 않고 물어봅니다. 답은 이 사이트가 아는 자료에서 찾아 출처와 함께 보여 줍니다.
          </p>
        </div>
      </div>

      <div className="chat">
        <div className="chat-log">
          {msgs.map((m, i) => (
            <div key={i} className={`bubble ${m.role}`}>
              {m.role === "agent" && <span className="brand-mark" aria-hidden="true" />}
              <div className="bubble-body">
                {!m.model && <div>{m.text}</div>}
                {m.body}
                {m.sources && m.sources.length > 0 && (
                  <div className="bubble-src">
                    출처:{" "}
                    {m.sources.map((s) => (
                      <a key={s.hash} href={s.hash} onClick={(e) => { e.preventDefault(); navigate(s.hash); }}>{s.label}</a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {busy && <div className="bubble agent"><span className="brand-mark" aria-hidden="true" /><div className="bubble-body muted">찾는 중…</div></div>}
          <div ref={endRef} />
        </div>

        <div className="chat-suggest">
          {SUGGEST.map((s) => (
            <button key={s} className="chip" onClick={() => void ask(s)}>{s}</button>
          ))}
        </div>

        <div className="chat-input">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void ask(input); }}
            placeholder="예: JTM2UB0130583 어디 있어?  ·  이번 주 출고 몇 건?  ·  나래넷츠 케이스"
            autoFocus
          />
          <button className="primary" disabled={busy || !input.trim()} onClick={() => void ask(input)}>묻기</button>
        </div>
        <p className="muted" style={{ fontSize: 12, margin: "8px 0 0" }}>
          에이전트는 이 사이트가 아는 자료(케이스·장비·교체·출고·달력·협력사·학습된 장비 명령)를 읽고 답합니다. 답마다 출처를 붙입니다.
        </p>
      </div>
    </>
  );
}
